import { useNavigate } from "react-router-dom";
import { useRef } from "react";

const headings = [
    { id: "doctors-jobs", label: "Doctors’ Jobs" },
    { id: "ownership", label: "Ownership and Affiliation" },
    { id: "ip", label: "Intellectual Property and Copyright" },
    { id: "cv-analyser", label: "CV Analyser Tool" },
    { id: "privacy", label: "Privacy and Third-Party Services" },
    { id: "liability", label: "Limitation of Liability" },
    { id: "agreement", label: "Agreement and Updates" },
    { id: "takeaways", label: "Key Takeaways" },
];

const DisclaimerPage = () => {
    const sectionRefs = useRef(
        headings.reduce((acc, h) => {
            acc[h.id] = null;
            return acc;
        }, {})
    );

    const handleNavClick = (id) => {
        const ref = sectionRefs.current[id];
        if (ref) {
            ref.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const setSectionRef = (id) => (el) => {
        sectionRefs.current[id] = el;
    };

    const navigate = useNavigate();

    return (
        <div className="min-h-screen w-full bg-gray-100">
            {/* Logo at the top left */}
            <div className="max-w-6xl p-6 flex items-start sticky top-0"
                onClick={() => navigate("/")}>
                <div
                    className="logo cursor-pointer"
                >
                    <span className="logo-text">Doctors' Jobs</span> <span className="logo-badge">Beta</span>
                </div>
            </div>
            <div className="flex flex-col md:flex-row max-w-6xl mx-auto p-6 gap-8 pt-0">
                {/* Side Navigation */}
                <aside className="md:w-1/4 mb-6 md:mb-0">
                    <nav className="sticky top-24 bg-white rounded p-4">
                        <ul className="space-y-2">
                            {headings.map((h) => (
                                <li key={h.id}>
                                    <button
                                        type="button"
                                        className="text-left w-full hover:underline focus:outline-none"
                                        onClick={() => handleNavClick(h.id)}
                                    >
                                        {h.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </aside>
                {/* Main Content */}
                <main className="md:w-3/4 w-full">
                    <div className="max-w-2xl w-full p-0 mx-auto">
                        <h1 className="text-3xl font-bold mb-6 text-center">
                            Disclaimer
                        </h1>
                        <section
                            id="doctors-jobs"
                            className="mb-6 scroll-mt-24"
                            ref={setSectionRef("doctors-jobs")}
                        >
                            <h2 className="text-xl font-semibold mb-2">
                                Doctors’ Jobs
                            </h2>
                            <p className="text-gray-700">
                                Doctors’ Jobs provides a curated list of job roles along with links to application pages. While we strive to maintain accurate and up-to-date listings, we do not guarantee employment, the availability of any role, or that the companies advertising these roles are actively hiring or legitimate. It is your responsibility to thoroughly vet and research each company and role before applying.
                            </p>
                        </section>
                        <section
                            id="ownership"
                            className="mb-6 scroll-mt-24"
                            ref={setSectionRef("ownership")}
                        >
                            <h2 className="text-xl font-semibold mb-2">
                                Ownership and Affiliation
                            </h2>
                            <p className="text-gray-700">
                                Doctor’s Jobs is a proprietary product developed and owned by Four Minute Medicine (Pty) Ltd. All content, tools, and services offered on this platform are created and maintained by Four Minute Medicine for educational purposes.
                            </p>
                        </section>
                        <section
                            id="ip"
                            className="mb-6 scroll-mt-24"
                            ref={setSectionRef("ip")}
                        >
                            <h2 className="text-xl font-semibold mb-2">
                                Intellectual Property and Copyright
                            </h2>
                            <p className="text-gray-700">
                            All content delivered on this platform is the intellectual property of Four Minute Medicine (Pty) Ltd and is protected by applicable copyright laws. This includes, but is not limited to, videos, written materials, graphics, quizzes, assignments, and course structures.
                        </p>
                        <p className="text-gray-700 mt-2">
                        No part of this content may be copied, reproduced, distributed, republished, downloaded, displayed, posted, or transmitted in any form or by any means, including electronic, mechanical, photocopying, recording, or other, without the prior written permission of Four Minute Medicine.
                    </p>
                    <p className="text-gray-700 mt-2">
                        Unauthorised use of this content is strictly prohibited and may result in legal action.
                    </p>
                </section>
                <section
                    id="cv-analyser"
                    className="mb-6 scroll-mt-24"
                    ref={setSectionRef("cv-analyser")}
                >
                    <h2 className="text-xl font-semibold mb-2">
                    CV Analyser Tool
                </h2>
                <p className="text-gray-700">
                    Our CV analyser is a paid subscription service that uses AI technology to provide feedback and suggestions to improve your CV. While this tool aims to help you enhance your application materials, Doctors’ Jobs does not guarantee employment or job offers as a result of using the service.
                </p>
                <p className="text-gray-700 mt-2">
                    We may also recommend courses and other resources to support your career development. These recommendations are intended as general guidance and should not be considered professional career advice.
                </p>
            </section>
            <section
                id="privacy"
                className="mb-6 scroll-mt-24"
                ref={setSectionRef("privacy")}
            >
                <h2 className="text-xl font-semibold mb-2">
                    Privacy and Third-Party Services
                </h2>
                <p className="text-gray-700">
                    We are committed to protecting your privacy. For detailed information on how we collect, use, and protect your data, please refer to our Privacy Policy. Please note that some services on our platform use third-party tools and services. We recommend reviewing our Privacy Policy for more details regarding these.
                </p>
            </section>
            <section
                id="liability"
                className="mb-6 scroll-mt-24"
                ref={setSectionRef("liability")}
            >
                <h2 className="text-xl font-semibold mb-2">
                    Limitation of Liability
                </h2>
                <p className="text-gray-700">
                    To the fullest extent permitted by law, Doctors’ Jobs excludes liability for any loss, damage, or other consequences arising from your use of the job listings, CV analyser, or any related services. Nothing in this disclaimer limits liability where it would be unlawful to do so.
                </p>
            </section>
            <section
                id="agreement"
                className="mb-6 scroll-mt-24"
                ref={setSectionRef("agreement")}
            >
                <h2 className="text-xl font-semibold mb-2">
                    Agreement and Updates
                </h2>
                <p className="text-gray-700">
                    By using our services, you acknowledge and agree to this disclaimer, as well as our Terms of Service and Privacy Policy. We may update this disclaimer periodically; the most current version will always be accessible on our website.
                </p>
            </section>
            <section
                id="takeaways"
                className="scroll-mt-24"
                ref={setSectionRef("takeaways")}
            >
                <h2 className="text-xl font-semibold mb-2">
                    Key Takeaways
                </h2>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>
                        Job listings are provided for convenience only; no guarantee of employment or authenticity.
                    </li>
                    <li>
                        The CV analyser is a paid service offering AI-based feedback but does not guarantee job offers.
                    </li>
                    <li>
                        Recommendations are general guidance, not professional career advice.
                    </li>
                    <li>
                        Liability is limited except where prohibited by law.
                    </li>
                </ul>
            </section>
        </div>
                </main >
            </div >
        </div >
    );
};

export default DisclaimerPage;
